import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
    assertInviteCopied,
    assertInviteGenerated,
    getRemainingInvites,
    type InviteAcceptCode,
    type InviteAcceptUrl,
} from '@pkg/components/deprecated';
import { getErrorMessage, noop } from '@pkg/utils';

import { Groups } from '@app/client';
import { addNotification } from '@app/store/actions/notification';

import { useAppDispatch } from '../redux';

import { useCreateInvite } from './useCreateInvite';
import { usersCacheKeys } from './utilities';

export function useGetInviteData() {
    return useQuery({ queryKey: usersCacheKeys.inviteData, queryFn: Groups.callGetInviteData });
}

// TODO: Neaten up this hook, split into smaller hooks?
export function useGenerateInvite() {
    const dispatch = useAppDispatch();
    const [translate] = useTranslation('invites');
    const { data: inviteData } = useGetInviteData();
    const { mutate: createInvite, isPending: loadingInvite } = useCreateInvite();

    // TODO: Improve typing of `Error` response.
    const [inviteError, setInviteError] = useState<unknown>();
    const [inviteCode, setInviteCode] = useState<InviteAcceptCode>();
    const [clipboardUrl, setClipboardUrl] = useState<InviteAcceptUrl>();

    const invitesRemaining = getRemainingInvites(inviteData);
    const inviteGenerated = assertInviteGenerated(inviteCode);

    const inviteCopied = assertInviteCopied(clipboardUrl);
    const inviteGenerationDisabled = invitesRemaining === 0;

    const getCtaActionContent = () => {
        if (inviteGenerationDisabled) {
            return translate('invite_cta_disabled');
        }

        if (inviteCopied) {
            return translate('invite_cta_success');
        }

        return inviteGenerated ? inviteCode : translate('invite_cta_primary');
    };

    const ctaAriaLabel = useMemo(() => {
        if (inviteGenerationDisabled) {
            return translate('invite_aria_disabled');
        }

        if (inviteCopied) {
            return translate('invite_cta_success');
        }

        return inviteGenerated ? translate('invite_aria_code', { code: inviteCode }) : translate('invite_aria_primary');
    }, [inviteGenerationDisabled, inviteGenerated, inviteCopied, inviteCode, translate]);

    function shareInviteUrl(url: InviteAcceptUrl) {
        // TODO: Should we also include a `title`?
        if (navigator.share) navigator.share({ url }).then(noop, noop);

        // Found the solution from the following reference
        // https://stackoverflow.com/a/68241516
        setTimeout(async () => {
            try {
                await navigator.clipboard.writeText(url);
                setClipboardUrl(url);
            } catch {
                setInviteError(translate('invite_clipboard_error', { url }));
            }
        });
    }

    useEffect(() => {
        // TODO: The `notification` disappears too quickly to be useful,
        // and will also dismiss itself the moment it is interacted with.
        if (inviteError) {
            const message = getErrorMessage(inviteError);

            // TODO: Find a way to prevent this strange Safari iOS bug.
            if (message.includes('undefined is not an object')) {
                setInviteError(undefined);
            } else if (message.length) {
                // TODO: There is no callback to allow resetting `inviteError`
                // once the `notification` has been dismissed.
                dispatch(addNotification({ message }));
            }
        }
    }, [inviteError, dispatch]);

    function handleGenerateInvite(groupId?) {
        if (loadingInvite || inviteGenerationDisabled) return;

        if (clipboardUrl) return shareInviteUrl(clipboardUrl);
        if (inviteCode) return;

        setInviteError(undefined);

        createInvite(groupId, {
            //todo - mention typo from be
            onSuccess({ invitation_acceptence_url, invite_type }) {
                setInviteCode(invite_type.InviteCode.code);
                shareInviteUrl(invitation_acceptence_url);
            },
            onError(error) {
                setInviteError(error);
            },
        });
    }

    return {
        getCtaActionContent,
        ctaAriaLabel,
        handleGenerateInvite,
        inviteError,
        inviteGenerated,
        invitesRemaining,
        inviteGenerationDisabled,
    };
}
